import { Box, CircularProgress, type SxProps, type Theme } from "@mui/material";
import { useForkRef } from "@mui/material/utils";
import { type ReactNode, type Ref } from "react";

import { usePullToRefresh } from "./usePullToRefresh";

interface PullToRefreshProps {
  onRefresh: () => Promise<unknown> | unknown;
  disabled?: boolean;
  /** Forwarded to the scroll container so a caller can also observe it (e.g. infinite scroll). */
  scrollRef?: Ref<HTMLDivElement>;
  sx?: SxProps<Theme>;
  children: ReactNode;
}

// Finger travel (after resistance) at which the spinner reaches full opacity.
const SPINNER_REVEAL_DISTANCE = 56;

/**
 * Wraps a scrollable region with a pull-to-refresh gesture. Owns the scroll
 * container; pass `scrollRef` to also observe the element from outside.
 */
export function PullToRefresh({
  onRefresh,
  disabled = false,
  scrollRef: externalRef,
  sx,
  children,
}: PullToRefreshProps) {
  const { scrollRef, pullDistance, isRefreshing, isPulling } = usePullToRefresh<HTMLDivElement>({
    onRefresh,
    disabled,
  });
  const handleRef = useForkRef(scrollRef, externalRef);

  const spinnerProgress = Math.min(1, pullDistance / SPINNER_REVEAL_DISTANCE);

  return (
    <Box
      ref={handleRef}
      sx={[
        {
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          // Keep the pull gesture from chaining to the page/webview bounce.
          overscrollBehaviorY: "contain",
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        aria-hidden={!isRefreshing}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          height: `${pullDistance}px`,
          // Track the finger 1:1 while pulling; only ease the snap-back on release.
          transition: isPulling ? "none" : "height 0.15s ease-out",
        }}
      >
        <CircularProgress
          size={24}
          {...(isRefreshing ? {} : { variant: "determinate", value: spinnerProgress * 100 })}
          sx={{ opacity: spinnerProgress }}
        />
      </Box>
      {children}
    </Box>
  );
}
